import { queryOptions } from '@tanstack/react-query';

import APIold from '@api/graphqlOld';
import APIStage from '@api/graphqlStageOld';
import { ImgeFilterE } from '@schema/main';
import { withSsrCache } from '../../ssrCache';

/**
 * Список магазинов и регионы — почти статичные справочники.
 * Кэшируем на 10 минут: за это время queryClient.ensureQueryData
 * отдаёт данные из кэша без запроса, кто бы их ни спрашивал
 * (CacheDataRoute на каждой навигации, useShops, BarMenuStore и т.д.).
 */
const SHOPS_STALE_TIME = 10 * 60 * 1000; // 10 минут
const SHOPS_GC_TIME = 15 * 60 * 1000;

export const shopsQueryOptions = () =>
  withSsrCache(
    queryOptions({
      queryKey: ['shops'],
      queryFn: () => APIold.getShops(),
      staleTime: SHOPS_STALE_TIME,
      gcTime: SHOPS_GC_TIME
    })
  );

/** Бар по id — срез общего кэша getShops (шапка бар-заказа на «Спасибо за покупку») */
export const barByIdQueryOptions = (barId?: number | null) =>
  queryOptions({
    ...shopsQueryOptions(),
    select: res => res.getShops?.find(item => item.id === Number(barId)) ?? null
  });

export const activeStoreCitiesQueryOptions = () =>
  queryOptions({
    queryKey: ['activeStoreCities'],
    queryFn: () => APIold.getActiveStoreCityWithFields(),
    staleTime: SHOPS_STALE_TIME,
    gcTime: SHOPS_GC_TIME
  });

/**
 * Ближайшая доставка/самовывоз по магазинам (выпадашка в шапке).
 * Данные живут 5 минут — refetchInterval у подписчика заменяет
 * самописный polling из useStorePolling.
 */
export const AMOUNT_STORES_REFETCH_INTERVAL = 5 * 60 * 1000;

export const amountStoresQueryOptions = () =>
  queryOptions({
    queryKey: ['amountStores'],
    queryFn: () => APIStage.getAmountStores({}),
    staleTime: AMOUNT_STORES_REFETCH_INTERVAL,
    gcTime: AMOUNT_STORES_REFETCH_INTERVAL * 2
  });

/**
 * Точки самовывоза. cartToken — параметр, а не импорт из CartStore:
 * CartStore сам импортирует этот модуль, обратный импорт дал бы цикл.
 * Ярлык fastDelivery зависит от наличия — кэш короче справочника магазинов.
 */
export const pickupPointsQueryOptions = (cartToken: string) =>
  queryOptions({
    queryKey: ['pickupPoints', cartToken],
    queryFn: () =>
      APIStage.pickupPoints({
        cartToken,
        imageFilter: ImgeFilterE.W480
      }).then(res =>
        (res.pickupPoints ?? []).map(item => ({
          ...item,
          fastDelivery: item.has_amount === 1
        }))
      ),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  });

/**
 * Точка самовывоза по id (страница /store/:id). cartToken — параметр по той
 * же причине, что и выше. Ответ содержит deliveryIntervals — синк их в
 * CartStore (setIntervals) остаётся на стороне подписчика.
 */
/**
 * Карточка точки самовывоза /stores/:id.
 *
 * ЭТАП 2 (SSR) — cartToken СПЕЦИАЛЬНО не входит в ключ. На сервере он
 * недостижим: `cartToken` в CartStore это модульный `v1()`, сгенерированный
 * при старте процесса, и он не совпадёт ни с одним клиентским токеном из
 * localStorage. С токеном в ключе SSR-данные лежали бы под ключом, который
 * клиент никогда не прочитает: после гидрации карточка схлопывалась бы в
 * LoadingPage и грузилась заново — хуже, чем вообще без SSR.
 *
 * От токена зависит только «корзинная» часть ответа: при неизвестном токене
 * бэк (deliveryResolver.pickupPointById) выходит раньше расчёта интервалов —
 * `if (!(cartEntity instanceof CartModel)) return pickUpStores`. Поэтому
 * staleTime здесь 0: после гидрации клиент сам перезапрашивает точку уже со
 * своим токеном и дополняет интервалы, а карточка всё это время остаётся на
 * экране (данные в кэше есть, isLoading не поднимается).
 */
/**
 * ЭТАП 4 — ЭТОТ ЗАПРОС В ОБЩИЙ КЭШ НЕ ИДЁТ, хотя в плане он числился во
 * втором уровне. Причина вскрылась при включении: ключ у него безличный
 * (`['pickupPointById', storeId]` — cartToken из ключа убран специально,
 * серверный токен случайный и никогда не совпал бы с клиентским), а вот
 * ОТВЕТ — нет. Резолвер `deliveryResolver.pickupPointById` в LazarCart
 * поднимает корзину по токену и считает от неё доступность, тест-драйв и
 * минимальную сумму заказа. То есть значение зависит от пользователя, и
 * общий кэш по одному `storeId` отдал бы вариант одного клиента другому.
 *
 * ОТСЮДА ОБЩЕЕ ПРАВИЛО: ключ — НЕ единственный источник персональности.
 * Автоматическая проверка в `withSsrCache` смотрит только на ключ и такой
 * случай не поймает; перед включением кэша нужно смотреть ещё и на
 * аргументы `queryFn`.
 */
export const pickupPointByIdQueryOptions = (
  storeId: number,
  cartToken: string
) =>
  queryOptions({
    queryKey: ['pickupPointById', storeId],
    queryFn: () =>
      APIStage.pickupPointById({
        cartToken,
        storeId,
        imageFilter: ImgeFilterE.W480
      }),
    gcTime: 10 * 60 * 1000
  });

/**
 * Ближайшие слоты доставки/самовывоза. Ответ зависит от выбранного адреса
 * корзины на сервере (cartToken при этом не меняется), поэтому staleTime
 * короткий — после смены адреса данные обновятся максимум через минуту.
 */
export const nearestDeliveryQueryOptions = (cartToken: string) =>
  queryOptions({
    queryKey: ['nearestDelivery', cartToken],
    queryFn: () =>
      APIStage.nearestDelivery({ cartToken }).then(
        res => res.nearestDelivery ?? []
      ),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000
  });
